<template>
  <teleport to="body">
    <div class="container-fluid bb-popup-outer">
      <div class="bb-popup-bg d-flex justify-content-center align-items-center">
        <div
          class="card shadow bb-card bb-popup-card rounded bb-popup-table-card"
        >
          <div
            class="container-fluid p-4 bb-tooltip-wrapper bb-popup-table-body bb-ranged-skill-panel"
          >
            <div class="bb-popup-table-controls mb-4">
              <div class="row align-items-center flex-grow-1 g-3 w-100">
                <div class="col">
                  <div class="bb-page-title">Ranged Skill Calculator</div>
                </div>
                <div class="col-auto">
                  <div class="bb-popup-table-actions">
                    <button
                      type="button"
                      class="btn btn-lg btn-warning"
                      @click="handleCancel"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="isLoading"
              class="bb-loader bb-popup-table-loader"
            ></div>

            <div
              v-else
              class="bb-ranged-skill-calculator-body"
            >
              <div class="bb-ranged-skill-section mb-4">
                <div class="bb-ranged-skill-section-divider"></div>
                <div class="bb-ranged-skill-section-title mb-3">Self</div>

                <div class="row g-3 align-items-start">
                  <div class="col-12 col-xl-4">
                    <div class="form-floating">
                      <v-select
                        id="ranged-skill-weapon"
                        :modelValue="selectedWeaponKey"
                        :options="weaponOptions"
                        label="label"
                        :reduce="(option) => option.value"
                        class="form-control"
                        :clearable="true"
                        @update:modelValue="handleWeaponChange"
                      >
                        <template #search="{ attributes, events }">
                          <input
                            class="vs__search"
                            :required="!selectedWeaponKey"
                            v-bind="attributes"
                            v-on="events"
                          />
                        </template>
                      </v-select>
                      <label
                        class="bb-float-label"
                        for="ranged-skill-weapon"
                      >
                        Ranged Weapon
                      </label>
                    </div>
                  </div>

                  <div class="col-12 col-md-6 col-xl-3">
                    <div class="form-floating">
                      <input
                        id="ranged-skill-weapon-skill"
                        readonly
                        disabled
                        class="form-control"
                        :value="resolvedWeaponSkillName"
                      />
                      <label
                        class="bb-float-label optional"
                        for="ranged-skill-weapon-skill"
                      >
                        Weapon Skill
                      </label>
                    </div>
                  </div>

                  <div class="col-12 col-md-6 col-xl-2">
                    <div class="form-floating">
                      <input
                        id="ranged-skill-weapon-range"
                        readonly
                        disabled
                        class="form-control text-center"
                        :value="resolvedWeaponRange"
                      />
                      <label
                        class="bb-float-label optional"
                        for="ranged-skill-weapon-range"
                      >
                        Max Range (m)
                      </label>
                    </div>
                  </div>

                  <div class="col-12 col-md-6 col-xl-3">
                    <CounterEmit
                      pass_ref="ranged-skill-current-skill"
                      pass_label="Current Skill %"
                      :pass_count="currentSkill"
                      :pass_min="0"
                      :pass_max="200"
                      :pass_digits="3"
                      @change="onCounterChanged('currentSkill', $event)"
                    />
                  </div>
                </div>

                <div class="row g-3 mt-2">
                  <div class="col-12 col-md-6 col-xl-3">
                    <div class="form-floating bb-ranged-capability-field">
                      <input
                        readonly
                        disabled
                        class="form-control"
                        value=""
                        id="prone-capable"
                      />
                      <div class="bb-ranged-capability-value">
                        <img
                          v-if="resolvedWeaponProneCapable"
                          :src="tickImg"
                          class="bb-ranged-capability-icon"
                          alt="prone capable"
                        />
                      </div>
                      <label
                        class="bb-float-label optional"
                        for="prone-capable"
                        >Prone Capable</label
                      >
                    </div>
                  </div>
                  <div class="col-12 col-md-6 col-xl-3">
                    <div class="form-floating bb-ranged-capability-field">
                      <input
                        readonly
                        disabled
                        class="form-control"
                        value=""
                      />
                      <div class="bb-ranged-capability-value">
                        <img
                          v-if="resolvedWeaponStabilisedCapable"
                          :src="tickImg"
                          class="bb-ranged-capability-icon"
                          alt="stabilised capable"
                        />
                      </div>
                      <label class="bb-float-label optional"
                        >Stabilised Capable</label
                      >
                    </div>
                  </div>
                  <div class="col-12 col-md-6 col-xl-3">
                    <div class="form-floating bb-ranged-capability-field">
                      <input
                        readonly
                        disabled
                        class="form-control"
                        value=""
                      />
                      <div class="bb-ranged-capability-value">
                        <img
                          v-if="resolvedWeaponFlatTrajectory"
                          :src="tickImg"
                          class="bb-ranged-capability-icon"
                          alt="flat trajectory"
                        />
                      </div>
                      <label class="bb-float-label optional"
                        >Flat-Trajectory</label
                      >
                    </div>
                  </div>
                  <div
                    v-if="showWeaponUsableField"
                    class="col-12 col-md-6 col-xl-3"
                  >
                    <div class="form-floating bb-ranged-capability-field">
                      <input
                        readonly
                        disabled
                        class="form-control"
                        value=""
                      />
                      <div class="bb-ranged-capability-value">
                        <img
                          v-if="resolvedWeaponUsable"
                          :src="tickImg"
                          class="bb-ranged-capability-icon"
                          alt="usable"
                        />
                      </div>
                      <label class="bb-float-label optional">Usable</label>
                    </div>
                  </div>
                  <div
                    v-if="showAimsCounter"
                    class="col-12 col-md-6 col-xl-3 bb-ranged-skill-counter-col"
                  >
                    <CounterEmit
                      pass_ref="ranged-skill-aims"
                      pass_label="Aims"
                      :pass_count="aims"
                      :pass_min="0"
                      :pass_max="3"
                      :pass_digits="1"
                      @change="onCounterChanged('aims', $event)"
                    />
                  </div>
                </div>

                <div
                  v-if="showSelfControls"
                  class="row g-3 mt-2"
                >
                  <div
                    class="col-12 col-md-6 col-xl-3 d-flex bb-ranged-toggle-col bb-ranged-toggle-self-moving"
                  >
                    <enhanced-toggle
                      data-toggle-scope="self"
                      data-toggle-key="moving"
                      style="
                        --toggleWidth: 310px;
                        --labelWidth: 215px;
                        --switchWidth: 75px;
                        --switchPos: 215px;
                        height: 50px;
                        border-radius: 10px;
                        background-color: white;
                      "
                      :styleOff="`default`"
                      :styleOn="`primary`"
                      :labelOn="`Moving`"
                      :labelOff="`Not Moving`"
                      :checked="selfMoving"
                      @update:modelValue="
                        updateToggleSetting('selfMoving', $event)
                      "
                    />
                  </div>
                  <div
                    v-if="showStabilisedToggle"
                    class="col-12 col-md-6 col-xl-3 d-flex bb-ranged-toggle-col bb-ranged-toggle-self-stabilised"
                  >
                    <enhanced-toggle
                      data-toggle-scope="self"
                      data-toggle-key="stabilised"
                      style="
                        --toggleWidth: 310px;
                        --labelWidth: 215px;
                        --switchWidth: 75px;
                        --switchPos: 215px;
                        height: 50px;
                        border-radius: 10px;
                        background-color: white;
                      "
                      :styleOff="`default`"
                      :styleOn="`primary`"
                      :labelOn="`Stabilised`"
                      :labelOff="`Not Stabilised`"
                      :checked="stabilised"
                      @update:modelValue="
                        updateToggleSetting('stabilised', $event)
                      "
                    />
                  </div>
                  <div
                    class="col-12 col-md-6 col-xl-3 d-flex bb-ranged-toggle-col bb-ranged-toggle-self-unstable-ground"
                  >
                    <enhanced-toggle
                      data-toggle-scope="self"
                      data-toggle-key="unstable-ground"
                      style="
                        --toggleWidth: 310px;
                        --labelWidth: 215px;
                        --switchWidth: 75px;
                        --switchPos: 215px;
                        height: 50px;
                        border-radius: 10px;
                        background-color: white;
                      "
                      :styleOff="`default`"
                      :styleOn="`primary`"
                      :labelOn="`Unstable Ground`"
                      :labelOff="`Stable Ground`"
                      :checked="unstableGround"
                      @update:modelValue="
                        updateToggleSetting('unstableGround', $event)
                      "
                    />
                  </div>
                  <div
                    class="col-12 col-md-6 col-xl-3 d-flex bb-ranged-toggle-col bb-ranged-toggle-self-snapshot"
                  >
                    <enhanced-toggle
                      data-toggle-scope="self"
                      data-toggle-key="snapshot"
                      style="
                        --toggleWidth: 310px;
                        --labelWidth: 215px;
                        --switchWidth: 75px;
                        --switchPos: 215px;
                        height: 50px;
                        border-radius: 10px;
                        background-color: white;
                      "
                      :styleOff="`default`"
                      :styleOn="`primary`"
                      :labelOn="`Snapshot`"
                      :labelOff="`Not Snapshot`"
                      :checked="snapshot"
                      @update:modelValue="
                        updateToggleSetting('snapshot', $event)
                      "
                    />
                  </div>
                  <div
                    v-if="showProneToggle"
                    class="col-12 col-md-6 col-xl-3 d-flex bb-ranged-toggle-col bb-ranged-toggle-self-prone"
                  >
                    <enhanced-toggle
                      data-toggle-scope="self"
                      data-toggle-key="prone"
                      style="
                        --toggleWidth: 310px;
                        --labelWidth: 215px;
                        --switchWidth: 75px;
                        --switchPos: 215px;
                        height: 50px;
                        border-radius: 10px;
                        background-color: white;
                      "
                      :styleOff="`default`"
                      :styleOn="`primary`"
                      :labelOn="`Prone`"
                      :labelOff="`Not Prone`"
                      :checked="prone"
                      @update:modelValue="updateToggleSetting('prone', $event)"
                    />
                  </div>
                </div>
              </div>

              <div
                v-if="showTargetSection"
                class="bb-ranged-skill-section"
              >
                <div class="bb-ranged-skill-section-divider"></div>
                <div class="bb-ranged-skill-section-title mb-3">Target</div>

                <div class="row g-3 align-items-start">
                  <div
                    class="col-12 col-md-6 col-xl-3 d-flex bb-ranged-toggle-col bb-ranged-toggle-target-moving"
                  >
                    <enhanced-toggle
                      data-toggle-scope="target"
                      data-toggle-key="moving"
                      style="
                        --toggleWidth: 310px;
                        --labelWidth: 215px;
                        --switchWidth: 75px;
                        --switchPos: 215px;
                        height: 50px;
                        border-radius: 10px;
                        background-color: white;
                      "
                      :styleOff="`default`"
                      :styleOn="`primary`"
                      :labelOn="`Moving`"
                      :labelOff="`Stationary`"
                      :checked="targetMoving"
                      @update:modelValue="
                        updateToggleSetting('targetMoving', $event)
                      "
                    />
                  </div>
                  <div
                    class="col-12 col-md-6 col-xl-3 d-flex bb-ranged-toggle-col bb-ranged-toggle-target-helpless-surprised"
                  >
                    <enhanced-toggle
                      data-toggle-scope="target"
                      data-toggle-key="helpless-surprised"
                      style="
                        --toggleWidth: 310px;
                        --labelWidth: 215px;
                        --switchWidth: 75px;
                        --switchPos: 215px;
                        height: 50px;
                        border-radius: 10px;
                        background-color: white;
                      "
                      :styleOff="`default`"
                      :styleOn="`primary`"
                      :labelOn="`Helpless/Surprised`"
                      :labelOff="`Capable`"
                      :checked="targetHelplessSurprised"
                      @update:modelValue="
                        updateToggleSetting('targetHelplessSurprised', $event)
                      "
                    />
                  </div>
                  <div
                    class="col-12 col-md-6 col-xl-3 d-flex bb-ranged-toggle-col bb-ranged-toggle-target-prone"
                  >
                    <enhanced-toggle
                      data-toggle-scope="target"
                      data-toggle-key="prone"
                      style="
                        --toggleWidth: 310px;
                        --labelWidth: 215px;
                        --switchWidth: 75px;
                        --switchPos: 215px;
                        height: 50px;
                        border-radius: 10px;
                        background-color: white;
                      "
                      :styleOff="`default`"
                      :styleOn="`primary`"
                      :labelOn="`Prone`"
                      :labelOff="`Not Prone`"
                      :checked="targetProne"
                      @update:modelValue="
                        updateToggleSetting('targetProne', $event)
                      "
                    />
                  </div>
                  <div
                    class="col-12 col-md-6 col-xl-3 d-flex bb-ranged-toggle-col bb-ranged-toggle-target-obscured"
                  >
                    <enhanced-toggle
                      data-toggle-scope="target"
                      data-toggle-key="obscured"
                      style="
                        --toggleWidth: 310px;
                        --labelWidth: 215px;
                        --switchWidth: 75px;
                        --switchPos: 215px;
                        height: 50px;
                        border-radius: 10px;
                        background-color: white;
                      "
                      :styleOff="`default`"
                      :styleOn="`primary`"
                      :labelOn="`Obscured`"
                      :labelOff="`Not Obscured`"
                      :checked="targetObscured"
                      @update:modelValue="
                        updateToggleSetting('targetObscured', $event)
                      "
                    />
                  </div>
                  <div
                    v-if="targetMoving"
                    class="col-12 col-md-6 col-xl-3 d-flex bb-ranged-toggle-col bb-ranged-toggle-target-towards-away"
                  >
                    <enhanced-toggle
                      data-toggle-scope="target"
                      data-toggle-key="towards-away"
                      style="
                        --toggleWidth: 310px;
                        --labelWidth: 215px;
                        --switchWidth: 75px;
                        --switchPos: 215px;
                        height: 50px;
                        border-radius: 10px;
                        background-color: white;
                      "
                      :styleOff="`default`"
                      :styleOn="`primary`"
                      :labelOn="`Towards/Away`"
                      :labelOff="`Not Towards/Away`"
                      :checked="movingTowardsAway"
                      @update:modelValue="
                        updateToggleSetting('movingTowardsAway', $event)
                      "
                    />
                  </div>
                  <div
                    v-if="targetObscured"
                    class="col-12 col-md-6 col-xl-3 d-flex bb-ranged-toggle-col bb-ranged-toggle-target-heavily-obscured"
                  >
                    <enhanced-toggle
                      data-toggle-scope="target"
                      data-toggle-key="heavily-obscured"
                      style="
                        --toggleWidth: 310px;
                        --labelWidth: 215px;
                        --switchWidth: 75px;
                        --switchPos: 215px;
                        height: 50px;
                        border-radius: 10px;
                        background-color: white;
                      "
                      :styleOff="`default`"
                      :styleOn="`primary`"
                      :labelOn="`Heavily`"
                      :labelOff="`Partially`"
                      :checked="targetHeavilyObscured"
                      @update:modelValue="
                        updateToggleSetting('targetHeavilyObscured', $event)
                      "
                    />
                  </div>
                </div>

                <div class="row g-3 mt-2 align-items-start">
                  <div
                    class="col-12 col-md-6 col-xl-3 bb-ranged-skill-counter-col"
                  >
                    <CounterEmit
                      pass_ref="ranged-skill-target-siz"
                      pass_label="SIZ"
                      :pass_count="targetSiz"
                      :pass_min="1"
                      :pass_max="30"
                      :pass_digits="2"
                      @change="onCounterChanged('targetSiz', $event)"
                    />
                  </div>
                  <div
                    class="col-12 col-md-6 col-xl-3 bb-ranged-skill-counter-col"
                  >
                    <CounterEmit
                      pass_ref="ranged-skill-distance"
                      pass_label="Distance (m)"
                      :pass_count="targetDistance"
                      :pass_min="0"
                      :pass_max="rangedConfig.max_target_distance"
                      :pass_digits="3"
                      @change="onCounterChanged('targetDistance', $event)"
                    />
                  </div>
                  <div
                    v-if="targetMoving"
                    class="col-12 col-md-6 col-xl-3 bb-ranged-skill-counter-col"
                  >
                    <CounterEmit
                      pass_ref="ranged-skill-target-mr"
                      pass_label="Target MR"
                      :pass_count="targetMr"
                      :pass_min="0"
                      :pass_max="28"
                      :pass_digits="2"
                      @change="onCounterChanged('targetMr', $event)"
                    />
                  </div>
                </div>
              </div>

              <div
                v-if="showResultSection"
                class="bb-ranged-skill-section mt-4"
              >
                <div class="bb-ranged-skill-section-divider"></div>
                <div class="bb-ranged-skill-section-title mb-3">Result</div>

                <div class="row g-3 align-items-start">
                  <div
                    v-if="targetMoving"
                    class="col-12 col-md-6 col-xl-4"
                  >
                    <div class="form-floating">
                      <input
                        id="ranged-skill-moving-target-speed-adj"
                        readonly
                        disabled
                        class="form-control text-center"
                        :value="movingTargetSpeedAdj"
                      />
                      <label
                        class="bb-float-label optional"
                        for="ranged-skill-moving-target-speed-adj"
                      >
                        Moving Target Speed Adj
                      </label>
                    </div>
                  </div>

                  <div class="col-12 col-md-6 col-xl-4">
                    <div class="form-floating">
                      <input
                        id="ranged-skill-moving-target-distance-adj"
                        readonly
                        disabled
                        class="form-control text-center"
                        :value="movingTargetDistanceAdj"
                      />
                      <label
                        class="bb-float-label optional"
                        for="ranged-skill-moving-target-distance-adj"
                      >
                        Target Distance Adj
                      </label>
                    </div>
                  </div>

                  <div class="col-12 col-md-6 col-xl-4">
                    <ToolTip
                      v-if="showRequiredPercentTooltip"
                      :text="requiredPercentTooltipText"
                      position="top"
                      :tableWrapperMode="true"
                      :maxWidth="1320"
                      textAlign="left"
                    >
                      <div class="form-floating bb-ranged-tooltip-field">
                        <input
                          id="ranged-skill-required-percent"
                          readonly
                          disabled
                          :class="requiredPercentInputClass"
                          :style="requiredPercentInputStyle"
                          :value="requiredPercent"
                        />
                        <label
                          class="bb-float-label optional bb-ranged-required-percent-label"
                          for="ranged-skill-required-percent"
                        >
                          Roll Required %
                          <span class="bb-orange-circle"></span>
                        </label>
                      </div>
                    </ToolTip>
                    <div
                      v-else
                      class="form-floating bb-ranged-tooltip-field"
                    >
                      <input
                        id="ranged-skill-required-percent"
                        readonly
                        disabled
                        :class="requiredPercentInputClass"
                        :style="requiredPercentInputStyle"
                        :value="requiredPercent"
                      />
                      <label
                        class="bb-float-label optional bb-ranged-required-percent-label"
                        for="ranged-skill-required-percent"
                      >
                        Roll Required %
                      </label>
                    </div>
                  </div>
                </div>

                <div
                  v-if="showImpossibleMessage"
                  class="row mt-3"
                >
                  <div class="col-12">
                    <div class="bb-error-wrap">
                      {{ impossibleMessageText }}
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-if="displayErrorMessages.length"
                class="bb-ranged-skill-errors mt-4"
              >
                <div class="row mt-2 mb-3">
                  <div class="form-group">
                    <div class="row">
                      <div class="col me-3">
                        <div
                          v-for="errorMessage in displayErrorMessages"
                          :key="errorMessage"
                          class="bb-error-wrap bb-ranged-skill-error-message"
                        >
                          <div class="row mb-3">
                            {{ errorMessage }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { mapGetters } from 'vuex'
import { axios } from '../common/api.service.js'
import CounterEmit from '../components/CounterEmit.vue'
import ToolTip from '../components/ToolTip.vue'
import tickImg from '../assets/images/bbTick.png'
import {
  getEffectiveItemType,
  getItemPropertySource,
} from '../helpers/getEffectiveItemType.js'
import { evaluateWeaponUsability } from '../helpers/weaponUsabilityCore'

const normalizeWeaponName = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()

export default {
  name: 'RangedSkillCalculatorView',
  components: {
    CounterEmit,
    ToolTip,
  },
  setup() {
    return {
      tickImg,
    }
  },
  props: {
    character: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      errorMessages: [],
      staticItems: [],
      selectedWeaponKey: '',
      currentSkill: 0,
      aims: 0,
      targetMr: 0,
      targetSiz: 10,
      targetDistance: 20,
      selfMoving: false,
      stabilised: false,
      prone: false,
      snapshot: false,
      unstableGround: false,
      targetMoving: false,
      movingTowardsAway: true,
      targetHelplessSurprised: false,
      targetProne: false,
      targetObscured: false,
      targetHeavilyObscured: false,
      previousBodyOverflow: '',
      previousHtmlOverflow: '',
    }
  },
  computed: {
    ...mapGetters('characters', {
      storeGetStaticItemById: 'getStaticItemById',
    }),
    ...mapGetters('combat', {
      getConfig: 'getConfig',
    }),
    hasCharacterContext() {
      return Boolean(this.character?.id)
    },
    rangedConfig() {
      const config =
        typeof this.getConfig === 'function'
          ? this.getConfig()
          : this.getConfig || {}

      return {
        max_target_distance: Number(config.max_target_distance) || 350,
        moving_target_ranged_distance_low:
          Number(config.moving_target_ranged_distance_low) || 10,
        moving_target_ranged_distance_high:
          Number(config.moving_target_ranged_distance_high) || 20,
        moving_target_ranged_distance_low_per:
          Number(config.moving_target_ranged_distance_low_per) || 1,
        moving_target_ranged_distance_high_per:
          Number(config.moving_target_ranged_distance_high_per) || 2.5,
        moving_target_ranged_distance_other_per:
          Number(config.moving_target_ranged_distance_other_per) || 5,
      }
    },
    emptyWeaponMessage() {
      return this.hasCharacterContext
        ? 'Character has no ranged weapons'
        : 'No ranged weapons available.'
    },
    staticRangedWeapons() {
      const items = Array.isArray(this.staticItems) ? this.staticItems : []

      return [...items]
        .filter(
          (item) => this.getStaticItemEffectiveType(item) === 'Ranged Weapon',
        )
        .sort((left, right) =>
          String(left?.name || '').localeCompare(String(right?.name || '')),
        )
    },
    characterRangedWeapons() {
      const items = Array.isArray(this.character?.items)
        ? this.character.items
        : []

      return items
        .filter((item) => this.isCharacterRangedWeapon(item))
        .sort((left, right) =>
          String(this.getCharacterItemName(left)).localeCompare(
            String(this.getCharacterItemName(right)),
          ),
        )
    },
    weaponOptions() {
      const sourceItems = this.hasCharacterContext
        ? this.characterRangedWeapons
        : this.staticRangedWeapons

      return sourceItems.map((item) => ({
        value: this.getWeaponOptionValue(item),
        label: this.hasCharacterContext
          ? this.getCharacterItemName(item)
          : String(item?.name || ''),
      }))
    },
    selectedCharacterWeapon() {
      if (!this.hasCharacterContext) {
        return null
      }

      return (
        this.characterRangedWeapons.find(
          (item) => this.getWeaponOptionValue(item) === this.selectedWeaponKey,
        ) || null
      )
    },
    resolvedWeapon() {
      const selectedWeapon = this.hasCharacterContext
        ? this.resolveStaticWeaponForCharacterItem(this.selectedCharacterWeapon)
        : this.staticRangedWeapons.find(
            (item) =>
              this.getWeaponOptionValue(item) === this.selectedWeaponKey,
          ) || null

      return (
        getItemPropertySource(selectedWeapon, this.getStaticItemById) ||
        selectedWeapon ||
        null
      )
    },
    resolvedWeaponSkillName() {
      const firstSkill = Array.isArray(this.resolvedWeapon?.skills)
        ? this.resolvedWeapon.skills[0]
        : null

      return firstSkill?.name || ''
    },
    resolvedWeaponRange() {
      const range = this.resolvedWeapon?.range
      return range === null || range === undefined ? '' : String(range)
    },
    resolvedWeaponProneCapable() {
      return Boolean(this.resolvedWeapon?.prone_capable)
    },
    resolvedWeaponStabilisedCapable() {
      return Boolean(this.resolvedWeapon?.stabilised_capable)
    },
    resolvedWeaponFlatTrajectory() {
      return Boolean(this.resolvedWeapon?.flat_trajectory)
    },
    hasPositiveCurrentSkill() {
      return Number(this.currentSkill) > 0
    },
    showAimsCounter() {
      return (
        this.hasPositiveCurrentSkill && !this.selfMoving && !this.targetMoving
      )
    },
    showSelfControls() {
      return this.hasPositiveCurrentSkill
    },
    showTargetSection() {
      return this.hasPositiveCurrentSkill
    },
    showResultSection() {
      return this.hasPositiveCurrentSkill
    },
    showWeaponUsableField() {
      return this.hasCharacterContext
    },
    showStabilisedToggle() {
      return Boolean(this.resolvedWeapon?.stabilised_capable)
    },
    showProneToggle() {
      return Boolean(this.resolvedWeapon?.prone_capable)
    },
    weaponUsabilityResult() {
      if (!this.hasCharacterContext || !this.resolvedWeapon) {
        return null
      }

      return evaluateWeaponUsability({
        staticSkills: Array.isArray(this.resolvedWeapon?.skills)
          ? this.resolvedWeapon.skills
          : [],
        characterSkills: Array.isArray(this.character?.skills)
          ? this.character.skills
          : [],
        itemType: this.getStaticItemEffectiveType(this.resolvedWeapon),
        strCur: Number(this.character?.str_cur ?? null),
        dexCur: Number(this.character?.dex_cur ?? null),
      })
    },
    resolvedWeaponUsable() {
      return this.weaponUsabilityResult?.usable_status === 'Yes'
    },
    selectedWeaponUnusableMessage() {
      const result = this.weaponUsabilityResult

      if (!result) {
        return ''
      }

      return result.usable_status === 'Yes'
        ? ''
        : result.unusable_reasons ||
            'Required minimum characteristics are not met.'
    },
    selectedWeaponUnusableDisplayMessage() {
      return this.selectedWeaponUnusableMessage
        ? this.selectedWeaponUnusableMessage.replace(/\s*\n+\s*/g, ' - ')
        : ''
    },
    movingTargetSpeedAdj() {
      if (!this.targetMoving) {
        return 0
      }

      if (this.resolvedWeaponFlatTrajectory && this.movingTowardsAway) {
        return 0
      }

      const config = this.rangedConfig
      const distance = Number(this.targetDistance) || 0
      const mr = Number(this.targetMr) || 0

      let multiplier = config.moving_target_ranged_distance_other_per

      if (distance <= config.moving_target_ranged_distance_low) {
        multiplier = config.moving_target_ranged_distance_low_per
      } else if (distance <= config.moving_target_ranged_distance_high) {
        multiplier = config.moving_target_ranged_distance_high_per
      }

      return mr * multiplier * -1
    },
    movingTargetDistanceAdj() {
      const config = this.rangedConfig
      const distance = Number(this.targetDistance) || 0
      const postHighBandStep =
        config.moving_target_ranged_distance_high -
        config.moving_target_ranged_distance_low

      if (distance < config.moving_target_ranged_distance_low) {
        return (
          (config.moving_target_ranged_distance_low - distance) *
          config.moving_target_ranged_distance_other_per
        )
      }

      if (distance > config.moving_target_ranged_distance_high) {
        const stepSize =
          postHighBandStep || config.moving_target_ranged_distance_high || 1
        const distancePastHighBand =
          distance - config.moving_target_ranged_distance_high

        return (
          Math.ceil(distancePastHighBand / stepSize) *
          config.moving_target_ranged_distance_other_per *
          -1
        )
      }

      return 0
    },
    targetSizeAdj() {
      const targetSize = Number(this.targetSiz) || 0

      if (targetSize < 5) {
        return (targetSize - 5) * 10
      }

      if (targetSize > 20) {
        return Math.ceil((targetSize - 20) / 10) * 10
      }

      return 0
    },
    targetConditionAdj() {
      let adjustment = 0

      if (this.targetHelplessSurprised) {
        adjustment += 10
      }

      if (this.targetProne) {
        adjustment -= 20
      }

      if (this.targetObscured) {
        adjustment += this.targetHeavilyObscured ? -40 : -20
      }

      return adjustment
    },
    selfConditionAdj() {
      let adjustment = 0

      if (this.stabilised) {
        adjustment += 10
      }

      if (this.prone) {
        adjustment += this.resolvedWeaponFlatTrajectory ? 20 : -20
      }

      if (this.snapshot) {
        adjustment -= 20
      }

      if (this.unstableGround) {
        adjustment -= 20
      }

      return adjustment
    },
    aimsAdj() {
      const aimCount = Number(this.aims) || 0

      return aimCount > 0 ? aimCount * 10 : 0
    },
    selfModifierBreakdown() {
      return [
        { label: 'Aims', value: this.aimsAdj },
        { label: 'Stabilised', value: this.stabilised ? 10 : 0 },
        {
          label: this.resolvedWeaponFlatTrajectory
            ? 'Prone (flat-trajectory)'
            : 'Prone',
          value: this.prone
            ? this.resolvedWeaponFlatTrajectory
              ? 20
              : -20
            : 0,
        },
        { label: 'Snapshot', value: this.snapshot ? -20 : 0 },
        {
          label: 'Not Stable Ground',
          value: this.unstableGround ? -20 : 0,
        },
      ]
    },
    selfModifierSubtotal() {
      return this.selfModifierBreakdown.reduce(
        (total, entry) => total + entry.value,
        0,
      )
    },
    targetModifierBreakdown() {
      return [
        { label: 'SIZ', value: this.targetSizeAdj },
        {
          label: 'Helpless/Surprised',
          value: this.targetHelplessSurprised ? 10 : 0,
        },
        { label: 'Target Prone', value: this.targetProne ? -20 : 0 },
        {
          label: this.targetHeavilyObscured
            ? 'Heavily Obscured'
            : 'Partially Obscured',
          value: this.targetObscured
            ? this.targetHeavilyObscured
              ? -40
              : -20
            : 0,
        },
        { label: 'Moving Target Speed', value: this.movingTargetSpeedAdj },
        {
          label: 'Target Distance',
          value: this.movingTargetDistanceAdj,
        },
      ]
    },
    targetModifierSubtotal() {
      return this.targetModifierBreakdown.reduce(
        (total, entry) => total + entry.value,
        0,
      )
    },
    showRequiredPercentTooltip() {
      return Number(this.currentSkill) > 0
    },
    targetExceedsWeaponRange() {
      const maxRange = Number(this.resolvedWeapon?.range)
      const targetDistance = Number(this.targetDistance) || 0

      return (
        Number.isFinite(maxRange) && maxRange > 0 && targetDistance > maxRange
      )
    },
    requiredPercentTooltipText() {
      if (!this.showRequiredPercentTooltip) {
        return ''
      }

      const formatValue = (value) => `${value >= 0 ? '+' : ''}${value}`
      const formatTotalValue = (value) =>
        `${value > 0 ? value : formatValue(value)}`
      const selfLines = this.selfModifierBreakdown
        .filter((entry) => entry.value !== 0)
        .map((entry) => `${entry.label}: ${formatValue(entry.value)}`)
      const targetLines = this.targetModifierBreakdown
        .filter((entry) => entry.value !== 0)
        .map((entry) => `${entry.label}: ${formatValue(entry.value)}`)
      const lines = [`Current Skill: ${this.currentSkill}%`]

      if (selfLines.length > 0) {
        lines.push(
          `Self Modifiers (${formatValue(this.selfModifierSubtotal)}): ${selfLines.join(', ')}`,
        )
      }

      if (targetLines.length > 0) {
        lines.push(
          `Target Modifiers (${formatValue(this.targetModifierSubtotal)}): ${targetLines.join(', ')}`,
        )
      }

      lines.push(`Roll Required: ${formatTotalValue(this.requiredPercent)}%`)

      return lines.join('\n')
    },
    requiredPercent() {
      if (this.targetExceedsWeaponRange || this.selectedWeaponUnusableMessage) {
        return 0
      }

      return (
        (Number(this.currentSkill) || 0) +
        this.targetSizeAdj +
        this.targetConditionAdj +
        this.selfConditionAdj +
        this.aimsAdj +
        this.movingTargetSpeedAdj +
        this.movingTargetDistanceAdj
      )
    },
    requiredPercentInputStyle() {
      const isPositive = this.requiredPercent > 0

      return {
        backgroundColor: isPositive ? '#198754' : '#ff0000',
        borderColor: isPositive ? '#198754' : '#ff0000',
        color: '#ffffff',
        WebkitTextFillColor: '#ffffff',
        fontSize: '2.1rem',
        fontWeight: '400',
        lineHeight: '1.1',
        opacity: '1',
      }
    },
    requiredPercentInputClass() {
      return {
        'form-control': true,
        'text-center': true,
        'bb-ranged-required-percent': true,
        'bb-ranged-required-percent-positive': this.requiredPercent > 0,
        'bb-ranged-required-percent-negative': this.requiredPercent <= 0,
      }
    },
    impossibleMessageText() {
      const segments = [`*** Ranged skill not possible`]
      const hasSpecificImpossibleReason =
        Boolean(this.selectedWeaponUnusableDisplayMessage) ||
        this.targetExceedsWeaponRange

      if (this.selectedWeaponUnusableDisplayMessage) {
        segments.push(this.selectedWeaponUnusableDisplayMessage)
      }

      if (this.targetExceedsWeaponRange) {
        segments.push('Target range exceeds max for weapon')
      }

      if (!hasSpecificImpossibleReason) {
        segments[0] =
          '*** Ranged skill not possible. 01 required at DM discretion'
      }

      return segments.join(' - ')
    },
    showImpossibleMessage() {
      return Boolean(this.selectedWeaponKey && this.requiredPercent <= 0)
    },
    displayErrorMessages() {
      const messages = []

      if (Array.isArray(this.errorMessages)) {
        messages.push(...this.errorMessages.filter(Boolean))
      }

      if (!this.weaponOptions.length) {
        messages.push(this.emptyWeaponMessage)
      }

      return [...new Set(messages.filter(Boolean))]
    },
  },
  async mounted() {
    this.lockBackgroundScroll()
    await this.initializeView()
  },
  beforeUnmount() {
    this.restoreBackgroundScroll()
  },
  methods: {
    lockBackgroundScroll() {
      this.previousBodyOverflow = document.body.style.overflow || ''
      this.previousHtmlOverflow = document.documentElement.style.overflow || ''
      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'
    },
    restoreBackgroundScroll() {
      document.body.style.overflow = this.previousBodyOverflow
      document.documentElement.style.overflow = this.previousHtmlOverflow
    },
    async initializeView() {
      this.isLoading = true
      this.errorMessages = []

      try {
        if (!this.hasCharacterContext) {
          const rangedResponse = await axios.get(
            'api/v1/items/ranged-weapon/list/?limit=1000',
            {
              mode: 'same-origin',
            },
          )

          this.staticItems = this.normalizeItemsResponse(rangedResponse?.data)
        }

        this.initializeSelectedWeapon()
      } catch (error) {
        this.errorMessages = [
          window.bbUiErrorMsg(
            String(error || 'Unable to load ranged weapons.'),
          ),
        ]
      } finally {
        this.isLoading = false
      }
    },
    normalizeItemsResponse(data) {
      if (Array.isArray(data)) {
        return data
      }

      return Array.isArray(data?.results) ? data.results : []
    },
    getStaticItemById(id) {
      if (
        this.hasCharacterContext &&
        typeof this.storeGetStaticItemById === 'function'
      ) {
        return this.storeGetStaticItemById(id) || null
      }

      return (
        this.staticItems.find(
          (item) => String(item?.id || '') === String(id || ''),
        ) || null
      )
    },
    getStaticItemEffectiveType(item) {
      return getEffectiveItemType(item, this.getStaticItemById)
    },
    isCharacterRangedWeapon(item) {
      const type = String(item?.type || '')
        .trim()
        .toLowerCase()
      const artefactType = String(item?.artefact_type || '')
        .trim()
        .toLowerCase()
      const staticItem = this.resolveStaticWeaponForCharacterItem(item)

      return (
        type === 'ranged weapon' ||
        artefactType === 'ranged weapon' ||
        this.getStaticItemEffectiveType(staticItem) === 'Ranged Weapon'
      )
    },
    getCharacterItemName(item) {
      return String(
        item?.display_name || item?.name || item?.item_name || '',
      ).trim()
    },
    getWeaponOptionValue(item) {
      const id = item?.id ?? item?.item ?? ''
      const prefix = this.hasCharacterContext ? 'character' : 'static'
      const label = this.hasCharacterContext
        ? this.getCharacterItemName(item)
        : String(item?.name || '')
      return `${prefix}:${id}:${label}`
    },
    resolveStaticWeaponForCharacterItem(characterItem) {
      if (!characterItem) {
        return null
      }

      const directId = characterItem.item ?? null
      const directName = normalizeWeaponName(
        this.getCharacterItemName(characterItem),
      )

      return (
        this.getStaticItemById(directId) ||
        this.staticItems.find(
          (item) => String(item?.id || '') === String(directId || ''),
        ) ||
        this.staticItems.find(
          (item) => normalizeWeaponName(item?.name) === directName,
        ) ||
        null
      )
    },
    resolveDefaultCurrentSkill() {
      if (!this.hasCharacterContext || !this.resolvedWeaponSkillName) {
        return 0
      }

      const matchedSkill = (
        Array.isArray(this.character?.skills) ? this.character.skills : []
      ).find(
        (skill) => String(skill?.name || '') === this.resolvedWeaponSkillName,
      )

      return Number(matchedSkill?.cur) || 0
    },
    initializeSelectedWeapon() {
      const firstOption = this.weaponOptions[0] || null
      this.selectedWeaponKey = firstOption?.value || ''
      this.resetWeaponConditionToggles()
      this.currentSkill = this.resolveDefaultCurrentSkill()
      this.aims = 0
      this.targetMr = 0
      this.targetSiz = 10
      this.targetDistance = 20
      this.syncAimsForMovement()
    },
    handleWeaponChange(nextValue) {
      this.selectedWeaponKey = String(nextValue || '')
      this.resetWeaponConditionToggles()
      this.currentSkill = this.resolveDefaultCurrentSkill()
      this.syncAimsForMovement()
    },
    resetWeaponConditionToggles() {
      this.selfMoving = false
      this.stabilised = false
      this.prone = false
      this.snapshot = false
      this.unstableGround = false
      this.targetHelplessSurprised = false
      this.targetProne = false
      this.targetObscured = false
      this.targetHeavilyObscured = false
    },
    syncAimsForMovement() {
      if (this.selfMoving || this.targetMoving) {
        this.aims = 0
      }
    },
    updateToggleSetting(field, value) {
      this[field] = Boolean(value)

      if (field === 'targetMoving') {
        this.targetMr = this[field] ? 8 : 0
      }

      if (field === 'targetObscured' && !this[field]) {
        this.targetHeavilyObscured = false
      }

      if (field === 'selfMoving' || field === 'targetMoving') {
        this.syncAimsForMovement()
      }
    },
    onCounterChanged(field, event) {
      this[field] = Number(event?.count) || 0
    },
    handleCancel() {
      this.closeDialog()
    },
    closeDialog() {
      if (this.$route.name) {
        const query = { ...this.$route.query }
        delete query.popup

        if (
          this.$route.name === 'myCharactersView' ||
          this.$route.name === 'combatCharacters'
        ) {
          delete query.slug
        }

        this.$router.replace({
          name: this.$route.name,
          params: { ...this.$route.params },
          query,
        })
        return
      }

      this.$router.replace({ name: 'campaignsView' })
    },
  },
}
</script>

<style scoped>
.bb-popup-outer {
  position: fixed;
  inset: 0;
  z-index: 4000;
  overflow-y: auto;
}

.bb-popup-bg {
  min-height: calc(120dvh + 6rem);
  padding: 1rem 0.08rem;
  background: rgba(15, 23, 42, 0.58);
  align-items: center !important;
}

.bb-popup-table-card {
  width: min(1360px, 99vw);
  height: auto;
  min-height: calc(122dvh - 1rem);
  max-height: none;
  margin: 0 auto !important;
}

.bb-popup-table-body {
  display: flex;
  flex-direction: column;
  min-height: calc(118dvh - 1rem);
  max-height: none;
  overflow: visible;
  padding: 0.85rem 1.25rem !important;
}

.bb-ranged-toggle-col {
  justify-content: flex-start;
}

.bb-popup-table-controls {
  display: flex;
  align-items: center;
  margin-bottom: 0.85rem !important;
}

.bb-popup-table-actions {
  display: flex;
  justify-content: flex-end;
}

.bb-popup-table-card {
  min-height: calc(100dvh - 1.25rem);
}

.bb-ranged-skill-panel {
  min-height: calc(100dvh - 1.25rem);
}

.bb-ranged-skill-panel {
  padding: 2rem 2.4rem !important;
}

.bb-ranged-skill-section-title {
  font-size: 1.45rem;
  font-weight: 700;
  margin-bottom: 0.7rem !important;
}

.bb-ranged-skill-section-divider {
  width: 100%;
  height: 2px;
  margin: 1.1rem 0 0.6rem;
  background: rgba(148, 163, 184, 0.6);
}

.bb-ranged-skill-section.mb-4 {
  margin-bottom: 1rem !important;
}

.bb-ranged-skill-section.mt-4,
.bb-ranged-skill-errors.mt-4 {
  margin-top: 1rem !important;
}

.bb-ranged-skill-calculator-body {
  display: flex;
  flex: 1;
  flex-direction: column;
}

.bb-ranged-skill-calculator-body :deep(.form-floating > .form-control),
.bb-ranged-skill-calculator-body :deep(.form-floating > .form-select) {
  min-height: 58px;
}

.bb-ranged-capability-field .form-control {
  position: relative;
  padding-top: 2.625rem;
  padding-bottom: 0.25rem;
}

.bb-ranged-capability-field .bb-float-label.optional {
  color: #c2ccd6 !important;
}

.bb-ranged-capability-field > .bb-float-label.optional,
.bb-ranged-capability-field > .form-control:disabled ~ .bb-float-label.optional,
.bb-ranged-capability-field
  > .form-control:not(:placeholder-shown)
  ~ .bb-float-label.optional {
  opacity: 0.8;
  transform: scale(0.9) translateY(-0.5rem) translateX(0.15rem);
}

.bb-ranged-capability-value {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 2.7rem;
  padding-top: 0.55rem;
  pointer-events: none;
}

.bb-ranged-capability-icon {
  width: 46px;
  height: 46px;
  object-fit: contain;
}

.bb-ranged-required-percent {
  color: #fff !important;
  -webkit-text-fill-color: #fff;
  font-size: 2.1rem !important;
  font-weight: 400 !important;
  line-height: 1.1 !important;
  opacity: 1;
}

.bb-ranged-required-percent:disabled {
  color: #fff !important;
  -webkit-text-fill-color: #fff;
  font-size: 2.1rem !important;
  font-weight: 400 !important;
  line-height: 1.1 !important;
  opacity: 1;
}

.bb-ranged-tooltip-field {
  display: block;
}

.bb-ranged-required-percent-label {
  color: #fff !important;
  opacity: 1 !important;
}

.bb-ranged-tooltip-field > .bb-ranged-required-percent-label,
.bb-ranged-tooltip-field
  > .bb-ranged-required-percent:disabled
  ~ .bb-ranged-required-percent-label,
.bb-ranged-tooltip-field
  > .bb-ranged-required-percent:not(:placeholder-shown)
  ~ .bb-ranged-required-percent-label {
  color: #fff !important;
  opacity: 1 !important;
}

.bb-ranged-required-percent-positive {
  background: #198754 !important;
  border-color: #198754 !important;
  color: #fff !important;
}

.bb-ranged-required-percent-positive:disabled {
  background: #198754 !important;
  border-color: #198754 !important;
}

.bb-ranged-required-percent-negative {
  background: #ff0000 !important;
  border-color: #ff0000 !important;
  color: #fff !important;
}

.bb-ranged-required-percent-negative:disabled {
  background: #ff0000 !important;
  border-color: #ff0000 !important;
}

.bb-ranged-skill-errors {
  margin-top: auto;
}

.bb-ranged-skill-error-message + .bb-ranged-skill-error-message {
  margin-top: 0;
}

.bb-ranged-skill-counter-col {
  max-width: 330px;
}

@media (max-width: 767.98px) {
  .bb-popup-bg {
    min-height: calc(109dvh + 3rem);
    padding: 0.6rem 0.04rem;
  }

  .bb-popup-table-card {
    width: 100%;
    min-height: calc(116dvh - 0.7rem);
  }

  .bb-popup-table-body {
    min-height: calc(116dvh - 0.7rem);
    padding: 0.7rem 0.9rem !important;
  }

  .bb-ranged-skill-panel {
    min-height: calc(116dvh - 0.7rem);
    padding: 0.95rem 1.1rem !important;
  }

  .bb-ranged-skill-counter-col {
    max-width: none;
  }
}
</style>
